import { distance as turfDistance, point as turfPoint } from '@turf/turf';
import { PROFILE_LINE, TurnInstruction, isJoinEntry } from '../turnInstructions';
import { DirectionRow, buildDirectionRows } from './directionsList';
import { directionRowPointsSomewhere, turnNeighbours } from './maneuverIcon';
import { ManeuverSign, isTerminal, isUTurn } from './maneuverSign';
import {
  RouteManeuver,
  Segment,
  parseInstructions,
  seamOffsets,
  survivesCollapse
} from './plannerInstructionParser';
import { PointWithDistance, Seam, detectSeams } from './seamManeuverDetector';

/**
 * The turns a route being planned carries, in the shape the recording apps read them.
 *
 * The planner has the instructions before anything is saved — GraphHopper described each
 * segment as it was snapped, and they ride along on the line string. So the list can be shown
 * for a route that exists only on screen, and shows what the hiker will actually be told,
 * because it is the same arithmetic the phones run.
 *
 * Pure: line strings in, rows out. No store, no map.
 */

/** The minimum a line string has to expose for its turns to be placed. */
export interface DirectionsLineString {
  coordinates: number[][];
  instructions?: TurnInstruction[];
  profile?: string;
}

export function directionRowsFor(lineStrings: DirectionsLineString[]): DirectionRow[] {
  const reading = readRoute(lineStrings);
  if (!reading) return [];
  const { totalMeters, fromInstructions } = reading;

  const seams = reading.seams.map(
    (seam): RouteManeuver => ({
      distanceFromStartMeters: seam.distanceFromStartMeters,
      sign: seam.sign,
      text: '',
      isUserWorded: false,
      isHidden: false
    })
  );

  const visible = [...fromInstructions, ...seams].filter((m) => !m.isHidden);
  // Straight lines alone are not a list of turns.
  if (visible.length === 0) return [];

  const straightLines = reading.straightLines.map(
    ({ startMeters, lengthMeters }): RouteManeuver => ({
      distanceFromStartMeters: startMeters,
      sign: ManeuverSign.CONTINUE,
      text: '',
      isUserWorded: false,
      isHidden: false,
      straightLineMeters: lengthMeters
    })
  );

  // The route's end is known exactly, so take it rather than trusting the last instruction
  // to sit at it.
  const withFinish = [
    ...[...visible.filter((m) => !isTerminal(m.sign)), ...straightLines].sort(
      // A turn at the very start of a straight line is the turn onto it, so it reads first.
      (a, b) =>
        a.distanceFromStartMeters - b.distanceFromStartMeters ||
        Number(a.straightLineMeters !== undefined) - Number(b.straightLineMeters !== undefined)
    ),
    {
      distanceFromStartMeters: totalMeters,
      sign: ManeuverSign.FINISH,
      text: '',
      isUserWorded: false,
      isHidden: false
    } as RouteManeuver
  ];

  return buildDirectionRows(withFinish);
}

/**
 * The turns the list shows only because the route turns at a join, keyed by the index of the
 * line string the join ends. Deleting the marker there removes the join, so the merge has to
 * store the turn or it disappears.
 */
export function seamSignsByJoin(lineStrings: DirectionsLineString[]): Map<number, number> {
  const signs = new Map<number, number>();
  const reading = readRoute(lineStrings);
  if (!reading) return signs;

  reading.seams.forEach((seam) => {
    const join = reading.seamDistances.indexOf(seam.distanceFromStartMeters);
    if (join >= 0) signs.set(reading.segments[join].position, seam.sign);
  });
  return signs;
}

interface RouteReading {
  totalMeters: number;
  segments: Segment[];
  fromInstructions: RouteManeuver[];
  /** Every join, including the ones next to a straight line that are never read for a turn. */
  seamDistances: number[];
  seams: Seam[];
  straightLines: { startMeters: number; lengthMeters: number }[];
}

function isStraightLine(segment: Segment | undefined): boolean {
  return segment?.profile === PROFILE_LINE;
}

function readRoute(lineStrings: DirectionsLineString[]): RouteReading | null {
  // Indexed before filtering, not after: `position` becomes the address an edit is written
  // back to, so it has to be the line string's own index in the route. A route holding a
  // segment with no geometry would otherwise renumber everything after it and send the edit
  // to the wrong line.
  //
  // A segment with no geometry has nothing to measure its turns against, and including it
  // would hand its share of the route to the segments around it.
  const usable = lineStrings
    .map((ls, index) => ({ ls, index }))
    .filter(({ ls }) => (ls.coordinates?.length ?? 0) > 0);
  if (usable.length === 0) return null;

  const points = pointsWithDistance(usable.flatMap(({ ls }) => ls.coordinates));
  if (points.length < 2) return null;
  const totalMeters = points[points.length - 1].distanceMeters;

  const segments: Segment[] = usable.map(({ ls, index }) => ({
    position: index,
    // The segment's own geometry rather than a stored distance: a hand-drawn segment
    // carries no distance at all, and a zero there would hand its share of the route to the
    // segments around it, moving their turns earlier than the junctions they describe.
    lengthMeters: geometryLength(ls.coordinates),
    instructions: ls.instructions ?? [],
    profile: ls.profile
  }));

  // A straight line has no turn-by-turn: only what a person wrote on it is read, and no turn
  // is worked out from the geometry at either end of it.
  const straight = new Set(segments.filter(isStraightLine).map((segment) => segment.position));
  const fromInstructions = parseInstructions(segments, totalMeters).filter(
    (m) => m.cue !== undefined || !straight.has(m.origin?.segmentIndex ?? -1)
  );
  const seamDistances = seamOffsets(segments, totalMeters);
  // A marker GraphHopper has settled already carries its answer, turn or no turn.
  const readableSeams = seamDistances.filter(
    (_, join) =>
      !isStraightLine(segments[join]) &&
      !isStraightLine(segments[join + 1]) &&
      !(segments[join + 1].instructions[0] && isJoinEntry(segments[join + 1].instructions[0]))
  );

  // Suppressed entries are still in `fromInstructions` here, deliberately: the detector skips
  // any seam that already has a maneuver within its separation window, so a hidden entry
  // written at a join is what stops a deleted turn being synthesised straight back.
  const seams = detectSeams(
    points,
    readableSeams,
    fromInstructions.map((m) => m.distanceFromStartMeters),
    // The junction turns either side of a stub describe the junction, not the turnaround at its
    // tip. Only a turn that already sends the hiker back speaks for it — or anything a person
    // wrote or hid there, which keeps what it had.
    fromInstructions
      .filter((m) => survivesCollapse(m) || isUTurn(m.sign))
      .map((m) => m.distanceFromStartMeters)
  );

  const straightLines = segments.flatMap((segment, index) => {
    if (!isStraightLine(segment)) return [];
    const startMeters = index === 0 ? 0 : seamDistances[index - 1];
    const endMeters = index < seamDistances.length ? seamDistances[index] : totalMeters;
    return [{ startMeters, lengthMeters: endMeters - startMeters }];
  });

  return { totalMeters, segments, fromInstructions, seamDistances, seams, straightLines };
}

/** Whether a route has anything to list, so the tools entry can say so before opening. */
export function hasInstructions(lineStrings: DirectionsLineString[]): boolean {
  return lineStrings.some((ls) => (ls.instructions?.length ?? 0) > 0);
}

/**
 * Where on the route a row sits, as `[lng, lat]`.
 *
 * Measured over the same flattened vertices `directionRowsFor` positions turns against, so a
 * row's `distanceFromStartMeters` lands on the junction it describes.
 */
export function coordinateAtDistance(
  lineStrings: DirectionsLineString[],
  meters: number
): [number, number] | null {
  const points = routePoints(lineStrings);
  return points.length === 0 ? null : interpolate(points, meters);
}

const APPROACH_WINDOW_METERS = 25;
/** How far a row's measured distance may sit from the junction it describes. */
const CORNER_SEARCH_METERS = 15;
/** The legs either side of a candidate vertex, measured as chords so vertex jitter averages out. */
const CORNER_CHORD_METERS = 10;
/** Less of a heading change than this is a bend in the path, not the junction. */
const MIN_CORNER_DEGREES = 20;
/** A row this close to a vertex the line turns at is on its junction already, as a fresh GraphHopper turn is. */
const ON_CORNER_METERS = 2;
/** The shortest approach worth trusting, even when the previous turn is closer than this. */
const MIN_APPROACH_CHORD_METERS = 5;
/** Shorter than this there is no approach at all — the row is on the route's start. */
const MIN_CHORD_METERS = 1;
const METERS_PER_DEGREE_LAT = 111_320;

export interface TurnAnchor {
  /** Where the glyph goes, as `[lng, lat]` — the junction itself when one was found. */
  at: [number, number];
  /** Compass bearing of the walk into `at`, or null when there is nothing to turn it to. */
  bearing: number | null;
}

export interface TurnNeighbours {
  /** The previous turn's distance. Omitted for the first turn, whose approach is the route's start. */
  previousMeters?: number;
  /** The next turn's distance, if there is one. */
  nextMeters?: number;
}

/**
 * Where a row's glyph sits on the line, and which way it faces.
 *
 * A row's distance is not the junction. It is GraphHopper's per-segment `d` rescaled onto the
 * planner's geometry, and lands a few metres either side of the corner it describes. Measuring
 * the approach from there is what made the arrow sometimes perfect and sometimes nonsense: land
 * just past the corner and the chord takes in the outgoing leg; land on it and all is well.
 *
 * So the glyph is anchored to the geometry instead. Within a few metres of the row — never
 * reaching towards a neighbouring row's own corner — the vertex where the line changes heading
 * most is the junction. The approach is a chord of up to 25 m ending there (Android's
 * `RouteBearing.approaching`), shortened so it doesn't cross the previous turn's corner — but
 * never below 5 m, because a shorter chord is back to the vertex wobble the window avoids, and
 * a turn cue added a metre after a routed turn still has a direction. Only a row with no
 * approach at all, at the route's very start, faces along the leg it leaves on.
 *
 * With no clear corner nearby (a "continue", a cue on a straight) the row's own distance
 * stands. Only for rows that sit on a corner: the start, the arrival and a note are places, and
 * snapping one drags it onto a junction beside it — see `directionRowAnchor`. Pure, and one
 * pass over the route.
 */
export function turnAnchor(
  lineStrings: DirectionsLineString[],
  meters: number,
  neighbours: TurnNeighbours = {}
): TurnAnchor | null {
  const points = routePoints(lineStrings);
  if (points.length === 0) return null;
  const total = points[points.length - 1].distanceMeters;
  const target = Math.min(Math.max(0, meters), total);
  const { previousMeters, nextMeters } = neighbours;

  const window = {
    from: Math.max(target - CORNER_SEARCH_METERS, previousMeters === undefined ? 0 : (previousMeters + target) / 2),
    to: Math.min(target + CORNER_SEARCH_METERS, nextMeters === undefined ? total : (target + nextMeters) / 2)
  };
  const anchorMeters = cornerAt(points, target, window) ?? cornerNear(points, target, window);
  const at = interpolate(points, anchorMeters);

  // Halfway back to the previous turn rather than all the way: its distance is as approximate
  // as this one's, and a chord reaching it could still clip its corner. The minimum chord
  // overrides that, and the route's start overrides both.
  const halfway = previousMeters === undefined ? 0 : (previousMeters + anchorMeters) / 2;
  const approachFrom = Math.max(
    0,
    Math.min(anchorMeters - MIN_APPROACH_CHORD_METERS, Math.max(anchorMeters - APPROACH_WINDOW_METERS, halfway))
  );
  if (anchorMeters - approachFrom >= MIN_CHORD_METERS) {
    return { at, bearing: chordBearing(interpolate(points, approachFrom), at) };
  }

  // No approach to measure: the row sits at the route's start.
  const ahead = interpolate(points, Math.min(anchorMeters + APPROACH_WINDOW_METERS, nextMeters ?? total));
  return { at, bearing: chordBearing(at, ahead) };
}

/**
 * Where a row of the directions list is drawn, and which way its glyph faces.
 *
 * Only a row that points somewhere is snapped to a junction and turned. The start ring, the
 * arrival flag and a note are places on the line, not corners: snapped, the flag was pulled
 * back onto a turn in the route's last metres, the ring forward onto one in its first, and a
 * note onto the junction beside it. They stay at their own distance, upright.
 */
export function directionRowAnchor(
  lineStrings: DirectionsLineString[],
  rows: DirectionRow[],
  row: DirectionRow
): TurnAnchor | null {
  if (!directionRowPointsSomewhere(row)) {
    const at = coordinateAtDistance(lineStrings, row.distanceFromStartMeters);
    return at ? { at, bearing: null } : null;
  }
  return turnAnchor(lineStrings, row.distanceFromStartMeters, turnNeighbours(rows, row));
}

/** The vertex `target` sits on, if the line turns there edge to edge: `cornerNear`'s chords can make a bend just past it read sharper. */
function cornerAt(
  points: PointWithDistance[],
  target: number,
  window: { from: number; to: number }
): number | null {
  let nearest = -1;
  for (let i = 1; i < points.length - 1; i += 1) {
    const meters = points[i].distanceMeters;
    if (meters < window.from || meters > window.to) continue;
    if (nearest === -1 || Math.abs(meters - target) < Math.abs(points[nearest].distanceMeters - target)) nearest = i;
  }
  if (nearest === -1 || Math.abs(points[nearest].distanceMeters - target) > ON_CORNER_METERS) return null;

  const at = points[nearest];
  let before = nearest - 1;
  while (before >= 0 && points[before].distanceMeters >= at.distanceMeters) before -= 1;
  let after = nearest + 1;
  while (after < points.length && points[after].distanceMeters <= at.distanceMeters) after += 1;
  if (before < 0 || after >= points.length) return null;

  const incoming = chordBearing(points[before].coordinates as [number, number], at.coordinates as [number, number]);
  const outgoing = chordBearing(at.coordinates as [number, number], points[after].coordinates as [number, number]);
  if (incoming === null || outgoing === null) return null;
  return turnDegrees(incoming, outgoing) >= MIN_CORNER_DEGREES ? at.distanceMeters : null;
}

function turnDegrees(incoming: number, outgoing: number): number {
  return Math.abs(((outgoing - incoming + 540) % 360) - 180);
}

/** The distance of the sharpest corner within `[from, to]`, or `target` when there is none. */
function cornerNear(
  points: PointWithDistance[],
  target: number,
  window: { from: number; to: number }
): number {
  let best: { meters: number; degrees: number } | null = null;
  for (let i = 1; i < points.length - 1; i += 1) {
    const meters = points[i].distanceMeters;
    if (meters < window.from || meters > window.to) continue;

    const vertex = points[i].coordinates as [number, number];
    const incoming = chordBearing(interpolate(points, meters - CORNER_CHORD_METERS), vertex);
    const outgoing = chordBearing(vertex, interpolate(points, meters + CORNER_CHORD_METERS));
    if (incoming === null || outgoing === null) continue;

    const degrees = turnDegrees(incoming, outgoing);
    if (degrees < MIN_CORNER_DEGREES) continue;
    // Sharpest wins; between equals, the nearer one. A curve spread over several vertices
    // settles on its tightest point.
    if (
      !best ||
      degrees > best.degrees + 1 ||
      (Math.abs(degrees - best.degrees) <= 1 && Math.abs(meters - target) < Math.abs(best.meters - target))
    ) {
      best = { meters, degrees };
    }
  }
  return best ? best.meters : target;
}

function chordBearing(start: [number, number], end: [number, number]): number | null {
  return start[0] === end[0] && start[1] === end[1] ? null : flatBearing(start, end);
}

function interpolate(points: PointWithDistance[], meters: number): [number, number] {
  const target = Math.max(0, meters);
  for (let i = 1; i < points.length; i += 1) {
    const to = points[i];
    if (to.distanceMeters < target) continue;
    const from = points[i - 1];
    const span = to.distanceMeters - from.distanceMeters;
    const t = span > 0 ? (target - from.distanceMeters) / span : 0;
    return [
      from.coordinates[0] + (to.coordinates[0] - from.coordinates[0]) * t,
      from.coordinates[1] + (to.coordinates[1] - from.coordinates[1]) * t
    ];
  }
  const last = points[points.length - 1].coordinates;
  return [last[0], last[1]];
}

/** The route's vertices end to end, measured the way `directionRowsFor` positions turns. */
function routePoints(lineStrings: DirectionsLineString[]): PointWithDistance[] {
  return pointsWithDistance(
    lineStrings.filter((ls) => (ls.coordinates?.length ?? 0) > 0).flatMap((ls) => ls.coordinates)
  );
}

function flatBearing(start: [number, number], end: [number, number]): number {
  const metersPerDegreeLon = METERS_PER_DEGREE_LAT * Math.cos((((start[1] + end[1]) / 2) * Math.PI) / 180);
  const east = (end[0] - start[0]) * metersPerDegreeLon;
  const north = (end[1] - start[1]) * METERS_PER_DEGREE_LAT;
  return ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360;
}

function geometryLength(coordinates: number[][]): number {
  let total = 0;
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const step = stepMeters(coordinates[i], coordinates[i + 1]);
    if (Number.isFinite(step) && step > 0) total += step;
  }
  return total;
}

/** Distance along the route at each vertex, measured the way the turns were positioned. */
function pointsWithDistance(coordinates: number[][]): PointWithDistance[] {
  const points: PointWithDistance[] = [];
  let cumulative = 0;

  coordinates.forEach((coordinate, index) => {
    if (index > 0) {
      const step = stepMeters(coordinates[index - 1], coordinate);
      if (Number.isFinite(step) && step > 0) cumulative += step;
    }
    points.push({ coordinates: coordinate, distanceMeters: cumulative });
  });
  return points;
}

function stepMeters(a: number[], b: number[]): number {
  return turfDistance(turfPoint(a), turfPoint(b), { units: 'meters' });
}
